import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import {alpha} from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '../../theme/iconify/iconify';

import Notification from '../util/Notification';

import DocumentCard from './DocumentCard';
import DocumentUpload from './DocumentUpload';
import {mislecAPI} from "../../util/axios";

function Documents({ user, updateUser }) {
    const [documents, setDocuments] = React.useState([]);
    const [fetched, setFetched] = React.useState(false);
    const [uploadModal, setUploadModal] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        fetchDocuments();
    }, [user]);

    const fetchDocuments = async () => {
        if (!user) return;

        //  Fetch documents from the server
        mislecAPI.get(`/api/documents/${user._id}`)
            .then(res => {
                setDocuments(res.data.documents);
                setFetched(true);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    if (!fetched) {
        if (!user) navigate('/login');
        fetchDocuments();
    }

    const setModalState = (state) => {
        setUploadModal(state);
    }

    const reload = () => {
        fetchDocuments();
    }

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={5}>
                    <Typography variant='h4'>{t('documents.title')}</Typography>

                    <Button
                        variant=''
                        startIcon={<Iconify icon='eva:upload-fill' />}
                        onClick={() => setUploadModal(true)}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: 'rgba(33, 43, 54, 0.08)',
                            '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main'
                            }
                        }}
                    >
                        {t('documents.buttons.upload')}
                    </Button>
                </Stack>
                <Grid container spacing={3}>
                    {documents.length > 0 ?
                        user.role === 'admin' ?
                            <>
                                <Grid item xs={12}>
                                    <Typography variant='h5'>{t('documents.userDocuments')}</Typography>
                                </Grid>
                                {documents.filter(doc => doc.user === user._id).map(document => (
                                    <DocumentCard key={document._id} document={document} />
                                ))}
                                <Grid item xs={12}>
                                    <Typography variant='h5'>{t('documents.allDocuments')}</Typography>
                                </Grid>
                                {documents.filter(doc => doc.user !== user._id).map(document => (
                                    <DocumentCard key={document._id} document={document} />
                                ))}
                            </>
                            : documents.map(document => (
                                <DocumentCard key={document._id} document={document} />
                            ))
                        :
                        fetched ?
                            <Grid item xs={12}>
                                <Typography>{t('documents.noDocuments')}</Typography>
                            </Grid>
                            :
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                                    <CircularProgress />
                                </Box>
                            </Grid>
                    }
                </Grid>
            </Container>
            <Modal
                open={uploadModal}
                onClose={() => setModalState(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', md: '50%' }
                }}
            >
                <Paper
                    sx={{
                        padding: '24px',
                        minWidth: { xs: '80%', md: '50%' },
                        maxWidth: { xs: '95%', md: '50%' },
                    }}
                >
                    <DocumentUpload user={user} updateUser={updateUser} reload={reload} setModalState={setModalState} />
                </Paper>
            </Modal>
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Documents;